import { SUPPLIER_CATEGORIES, OTHER_CATEGORY_VALUE } from '@/data/site';

/**
 * Supplier Insights Survey content.
 *
 * Q1 is a multi-select question rendered separately by the wizard, so it is not
 * part of SURVEY_SINGLE_QUESTIONS. The conditional commission question is only
 * shown when Q7 is answered "Yes, regularly" or "Yes, occasionally", and it does
 * not count towards the ten core questions.
 */

export const SURVEY_CORE_TOTAL = 10;

export const Q1_KEY = 'q1_category';
export const Q1_PROMPT = 'Which category best describes your business?';

export const SURVEY_CATEGORY_OPTIONS = SUPPLIER_CATEGORIES;
export const SURVEY_OTHER_VALUE = OTHER_CATEGORY_VALUE;

export const Q7_KEY = 'q7_pays_commission';

export type SurveyQuestion = {
  key: string;
  number: number;
  prompt: string;
  options: string[];
};

export const SURVEY_SINGLE_QUESTIONS: SurveyQuestion[] = [
  {
    key: 'q2_ideal_customer',
    number: 2,
    prompt: 'What type of customer is ideal for your business?',
    options: [
      'Private celebrations and parties',
      'Weddings and special occasions',
      'Corporate and business events',
      'A mixture of event types',
    ],
  },
  {
    key: 'q3_booking_value',
    number: 3,
    prompt: 'What is the approximate value of an average booking?',
    options: ['Under \u00a3500', '\u00a3500\u2013\u00a3999', '\u00a31,000\u2013\u00a34,999', '\u00a35,000 and above'],
  },
  {
    key: 'q4_enquiry_source',
    number: 4,
    prompt: 'Where do most of your enquiries currently come from?',
    options: [
      'Recommendations and word of mouth',
      'Social media',
      'Website, Google or online directories',
      'Event planners, venues or business partners',
    ],
  },
  {
    key: 'q5_wasted_time',
    number: 5,
    prompt: 'Which enquiries waste the most time?',
    options: [
      'Customers with unrealistic budgets',
      'People who are just comparing prices',
      'Enquiries outside my area or services',
      'Enquiries with unclear requirements or no commitment',
    ],
  },
  {
    key: 'q6_referrals',
    number: 6,
    prompt: 'Do you currently receive referrals from other businesses?',
    options: ['Yes, regularly', 'Yes, occasionally', 'Very rarely', 'No, never'],
  },
  {
    key: Q7_KEY,
    number: 7,
    prompt: 'Do you currently pay referral fees or commissions?',
    options: ['Yes, regularly', 'Yes, occasionally', 'No, but I would consider it', 'No, and I prefer not to'],
  },
  {
    key: 'q8_success_fee',
    number: 8,
    prompt: 'Would you consider paying a success fee for a genuinely qualified booking?',
    options: ['Yes, definitely', 'Possibly, depending on the fee', 'I\u2019d need more information', 'No, I wouldn\u2019t'],
  },
  {
    key: 'q9_value_to_pay',
    number: 9,
    prompt: 'What would make a supplier introduction valuable enough to pay for?',
    options: [
      'Customer has a realistic, confirmed budget',
      'Customer is ready to make a booking',
      'The enquiry matches my services and availability',
      'A combination of all three',
    ],
  },
  {
    key: 'q10_growth_challenge',
    number: 10,
    prompt: 'What is your biggest challenge in growing your event business?',
    options: [
      'Finding enough new customers',
      'Attracting customers with suitable budgets',
      'Converting enquiries into confirmed bookings',
      'Building reliable referral relationships',
    ],
  },
];

/** Values of Q7 that trigger the optional commission follow-up. */
export const COMMISSION_TRIGGER_VALUES = ['Yes, regularly', 'Yes, occasionally'];

export const COMMISSION_QUESTION: SurveyQuestion = {
  key: 'q7b_commission_percentage',
  number: 0,
  prompt: 'If you currently pay referral commissions, what percentage do you typically pay?',
  options: [
    'Less than 5%',
    '5%\u20139%',
    '10%\u201314%',
    '15% or more',
    'It varies / I pay a fixed fee instead',
    'Prefer not to say',
  ],
};